/** Map backend apply-template error → user-safe copy. Never expose IDs / collections / stack traces. */
export interface SafeApplyError {
  title: string;
  message: string;
  allowRetry: boolean;
  variant: "warning" | "error" | "info";
  refreshCatalog?: boolean;
}

export function mapApplyError(err: unknown): SafeApplyError {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const data = (err as { response?: { data?: unknown } })?.response?.data ?? {};
  const detail = (data as { detail?: unknown }).detail;
  const detailObj = (detail && typeof detail === "object" ? detail : {}) as Record<string, unknown>;
  const code = typeof detailObj.code === "string"
    ? detailObj.code
    : (typeof detail === "string" ? detail : "");

  if (status === 404 || code === "template_not_found") {
    return {
      title: "Template unavailable",
      message: "The selected template is no longer available. The catalog will refresh.",
      allowRetry: false, variant: "warning", refreshCatalog: true,
    };
  }
  if (status === 410 || code === "template_inactive") {
    return {
      title: "Template no longer offered",
      message: "This template is no longer available for new setup. Please pick another option.",
      allowRetry: false, variant: "warning", refreshCatalog: true,
    };
  }
  if (code === "template_application_in_progress") {
    return {
      title: "Another administrator is applying a template",
      message: "Another administrator is currently applying a CRM template. Setup status will refresh when the operation finishes.",
      allowRetry: true, variant: "info",
    };
  }
  if (code === "template_change_requires_support") {
    return {
      title: "Support required to change template",
      message: "A different CRM template has already been applied to this tenant. Changing the CRM foundation requires RMBCS support.",
      allowRetry: false, variant: "warning",
    };
  }
  if (code === "template_upgrade_requires_support") {
    return {
      title: "Support required to upgrade template",
      message: "This tenant is pinned to its configured template version. Upgrades require RMBCS support.",
      allowRetry: false, variant: "warning",
    };
  }
  if (code === "template_application_requires_support") {
    const reason = typeof detailObj.reason === "string" ? detailObj.reason : "";
    if (reason === "crm_activity_present") {
      return {
        title: "Support required — existing CRM activity",
        message: "This tenant already has CRM activity. Setting up a template on it requires RMBCS support to avoid affecting existing records.",
        allowRetry: false, variant: "warning",
      };
    }
    if (reason === "config_drift") {
      return {
        title: "Support required — existing configuration detected",
        message: "This tenant already has configuration that a new template would replace. RMBCS support must review this before proceeding.",
        allowRetry: false, variant: "warning",
      };
    }
    if (reason === "rogue_config_drift") {
      return {
        title: "Support required — configuration mismatch",
        message: "Configuration on this tenant does not match a completed template application. Please contact RMBCS support.",
        allowRetry: false, variant: "warning",
      };
    }
    return {
      title: "Support required to apply template",
      message: "Please contact RMBCS support to complete template setup for this tenant.",
      allowRetry: false, variant: "warning",
    };
  }
  if (!status) {
    return {
      title: "Network problem",
      message: "The template application could not be confirmed. You can retry safely.",
      allowRetry: true, variant: "info",
    };
  }
  return {
    title: "Unable to apply template",
    message: "Something went wrong applying the template. Please retry or contact support.",
    allowRetry: true, variant: "error",
  };
}
